import React, { useState } from 'react';
import { 
    format, 
    addMonths, 
    subMonths, 
    startOfMonth, 
    endOfMonth, 
    startOfWeek, 
    endOfWeek, 
    isSameMonth, 
    isSameDay, 
    eachDayOfInterval,
    isBefore,
    startOfToday
} from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

interface CustomCalendarProps {
    availableSlots: string[];
    highlightedDates: string[];
    isLoadingSlots: boolean;
    onDateChange: (date: Date) => void;
    onSelectTime: (time: string) => void;
    onMonthChange: (date: Date) => void;
}

const CustomCalendar: React.FC<CustomCalendarProps> = ({ 
    availableSlots, 
    highlightedDates = [],
    isLoadingSlots, 
    onDateChange, 
    onSelectTime,
    onMonthChange
}) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date>(startOfToday());
    const [selectedTime, setSelectedTime] = useState<string | null>(null);

    const nextMonth = () => {
        const next = addMonths(currentMonth, 1);
        setCurrentMonth(next);
        onMonthChange(next);
    };
    const prevMonth = () => {
        const prev = subMonths(currentMonth, 1);
        setCurrentMonth(prev);
        onMonthChange(prev);
    };

    const onDateClick = (day: Date) => {
        if (isBefore(day, startOfToday())) return;
        setSelectedDate(day);
        setSelectedTime(null);
        onDateChange(day);
    };

    const renderHeader = () => {
        return (
            <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-6 tw-border-b tw-border-white/10">
                <div className="tw-flex tw-flex-col">
                    <span className="tw-text-xs tw-uppercase tw-tracking-widest tw-text-gray-500 tw-font-bold">Select Date</span>
                    <h2 className="tw-text-2xl tw-font-bold tw-text-white">{format(currentMonth, 'MMMM yyyy')}</h2>
                </div>
                <div className="tw-flex tw-gap-2">
                    <button 
                        onClick={prevMonth}
                        className="tw-p-2 tw-rounded-full tw-bg-white/5 hover:tw-bg-white/10 tw-border tw-border-white/10 tw-transition-colors"
                    >
                        <ChevronLeft className="tw-w-5 tw-h-5 tw-text-gray-400" />
                    </button>
                    <button 
                        onClick={nextMonth}
                        className="tw-p-2 tw-rounded-full tw-bg-white/5 hover:tw-bg-white/10 tw-border tw-border-white/10 tw-transition-colors"
                    >
                        <ChevronRight className="tw-w-5 tw-h-5 tw-text-gray-400" />
                    </button>
                </div>
            </div>
        );
    };

    const renderDays = () => {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return (
            <div className="tw-grid tw-grid-cols-7 tw-mb-2">
                {days.map((day) => (
                    <div key={day} className="tw-text-center tw-text-xs tw-font-bold tw-uppercase tw-text-gray-600 tw-py-2">
                        {day}
                    </div>
                ))}
            </div>
        );
    };

    const renderCells = () => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(monthStart);
        const startDate = startOfWeek(monthStart);
        const endDate = endOfWeek(monthEnd);

        const days = eachDayOfInterval({ start: startDate, end: endDate });
        const rows: any[] = [];
        let daysInRow: any[] = [];

        days.forEach((day, i) => {
            const formattedDate = format(day, 'd');
            const isDisabled = isBefore(day, startOfToday()) || !isSameMonth(day, monthStart);
            const isSelected = isSameDay(day, selectedDate);
            const isAvailable = highlightedDates.includes(format(day, 'yyyy-MM-dd'));

            daysInRow.push(
                <div
                    key={day.toString()}
                    className={`tw-relative tw-aspect-square tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-text-sm tw-font-medium tw-transition-all tw-duration-300 tw-rounded-xl
                        ${isDisabled ? 'tw-text-gray-700 tw-cursor-not-allowed' : 'tw-text-white hover:tw-bg-[#00C5FB]/10'}
                        ${isSelected ? 'tw-bg-[#00C5FB] tw-text-black hover:tw-bg-[#00C5FB]' : ''}
                    `}
                    onClick={() => onDateClick(day)}
                >
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
                        <span>{formattedDate}</span>
                        {isAvailable && !isDisabled && !isSelected && (
                            <motion.div 
                                animate={{ opacity: [0.4, 1, 0.4] }}
                                transition={{ duration: 2, repeat: Infinity }}
                                className="tw-w-1 tw-h-1 tw-bg-[#00C5FB] tw-rounded-full tw-shadow-[0_0_8px_rgba(0,197,251,0.8)]"
                            />
                        )}
                    </div>
                    {isSelected && (
                        <motion.div 
                            layoutId="activeDay"
                            className="tw-absolute tw-inset-0 tw-border-2 tw-border-[#00C5FB] tw-rounded-xl"
                            transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                        />
                    )}
                </div>
            );

            if ((i + 1) % 7 === 0) {
                rows.push(
                    <div className="tw-grid tw-grid-cols-7 tw-gap-1" key={day.toString()}>
                        {daysInRow}
                    </div>
                );
                daysInRow = [];
            }
        });

        return <div className="tw-flex tw-flex-col tw-gap-1">{rows}</div>;
    };

    const renderTimeSlots = () => {
        return (
            <div className="tw-flex tw-flex-col tw-h-full">
                <div className="tw-px-4 tw-py-6 tw-border-b tw-border-white/10 tw-flex tw-items-center tw-gap-2">
                    <Clock className="tw-w-5 tw-h-5 tw-text-[#00C5FB]" />
                    <span className="tw-text-lg tw-font-bold tw-text-white">Time Slots</span>
                </div>
                <div className="tw-relative tw-flex-1 tw-min-h-[300px]">
                    <AnimatePresence mode="wait">
                        {isLoadingSlots ? (
                            <motion.div
                                key="loader"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/20 tw-z-10"
                            >
                                <div className="tw-w-8 tw-h-8 tw-border-2 tw-border-[#00C5FB]/20 tw-border-t-[#00C5FB] tw-rounded-full tw-animate-spin" />
                            </motion.div>
                        ) : availableSlots.length === 0 ? (
                            <motion.div
                                key="empty"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-px-6 tw-text-center"
                            >
                                <p className="tw-text-gray-500 tw-text-sm">No available slots for this date. Please try another day.</p>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="slots"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="tw-grid tw-grid-cols-2 tw-gap-3 tw-p-6 tw-overflow-y-auto tw-max-h-[400px] no-scrollbar"
                            >
                                {availableSlots.map((timeString) => {
                                    const timeLabel = format(new Date(timeString), 'hh:mm a');
                                    return (
                                        <button
                                            key={timeString}
                                            onClick={() => {
                                                setSelectedTime(timeString);
                                                onSelectTime(timeString);
                                            }}
                                            className={`tw-py-3 tw-px-4 tw-rounded-xl tw-text-sm tw-font-medium tw-border tw-transition-all tw-duration-300
                                                ${selectedTime === timeString 
                                                    ? 'tw-bg-[#00C5FB] tw-text-black tw-border-[#00C5FB]' 
                                                    : 'tw-bg-white/5 tw-text-gray-300 tw-border-white/10 hover:tw-border-[#00C5FB]/50 hover:tw-bg-[#00C5FB]/5'}
                                            `}
                                        >
                                            {timeLabel}
                                        </button>
                                    );
                                })}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                <div className="tw-mt-auto tw-p-6 tw-bg-white/[0.02] tw-border-t tw-border-white/10">
                    <p className="tw-text-xs tw-text-gray-500 tw-text-center">
                        All times are in your local time ({(Intl.DateTimeFormat().resolvedOptions().timeZone)})
                    </p>
                </div>
            </div>
        );
    };

    return (
        <div className="tw-flex tw-flex-col md:tw-flex-row tw-w-full tw-min-h-[500px]">
            {/* Calendar Section */}
            <div className="tw-flex-1 tw-border-r tw-border-white/10 tw-p-4 md:tw-p-8">
                {renderHeader()}
                <div className="tw-mt-8">
                    {renderDays()}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentMonth.toString()}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            {renderCells()}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>

            {/* Time Slot Section */}
            <div className="tw-w-full md:tw-w-80 tw-bg-white/[0.01]">
                {renderTimeSlots()}
            </div>
        </div>
    );
};

export default CustomCalendar;
